import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, Loader2 } from 'lucide-react';
import { API, getAuthHeader } from '../../App';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const aed = (n) => Number(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MON = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monLabel = (k) => { const [y, m] = (k || '').split('-'); return MON[Number(m)] ? `${MON[Number(m)]} ${y}` : k; };

export const AutoSettledArrearsDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setOpen(true); setData(null); setPicked(new Set());
    try {
      const res = await axios.get(`${API}/talent/pms/arrears-tracker/auto-settled`, { headers: getAuthHeader() });
      setData(res.data); setPicked(new Set(res.data.accounts.map((a) => a.loan_account_id)));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load the list'); setOpen(false); }
  };

  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const reverse = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/talent/pms/arrears-tracker/auto-settled/reverse`, { loan_account_ids: [...picked] }, { headers: getAuthHeader() });
      toast.success(`${res.data.reversed_accounts} employee(s) set back to Pending - AED ${aed(res.data.total_reopened)} will be recovered in the next payroll`);
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reverse - nothing was changed'); }
    setBusy(false);
  };

  const pickedTotal = (data?.accounts || []).filter((a) => picked.has(a.loan_account_id)).reduce((s, a) => s + a.total, 0);

  return (
    <>
      <Button variant="outline" size="sm" className="gap-1" onClick={load} data-testid="arrears-auto-settled-btn">
        <RotateCcw className="w-4 h-4" /> Reverse auto-settled
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="arrears-auto-settled-dialog">
          <DialogHeader>
            <DialogTitle>Arrears marked "Recovered" automatically</DialogTitle>
            <DialogDescription>
              These were marked recovered by the system, not by a payroll or a manual entry. Ticked employees go back to Pending and are recovered in the next payroll.
              Check "Recoveries" against "Contract loan" before confirming - if a rider really paid in cash, untick them.
            </DialogDescription>
          </DialogHeader>
          {!data ? <div className="py-10 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
            : data.accounts.length === 0 ? <div className="py-10 text-center text-slate-500" data-testid="arrears-auto-settled-empty">Nothing to reverse - no automatic settlements found.</div>
              : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>{['', 'Employee', 'Arrears months', 'Amount', 'Contract loan', 'Recoveries'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((a) => (
                      <tr key={a.loan_account_id} className="border-t align-top" data-testid={`auto-settled-row-${a.loan_account_id}`}>
                        <td className="px-2 py-1.5"><Checkbox checked={picked.has(a.loan_account_id)} onCheckedChange={() => toggle(a.loan_account_id)} data-testid={`auto-settled-check-${a.loan_account_id}`} /></td>
                        <td className="px-2 py-1.5"><div className="font-medium">{a.rider_name || <span className="italic text-slate-400">Name not found (ID {a.rider_id})</span>}</div><div className="font-mono text-slate-400">{a.uid}</div></td>
                        <td className="px-2 py-1.5">{a.items.map((i) => <div key={i.payroll_month}>{monLabel(i.payroll_month)}: AED {aed(i.amount)}</div>)}</td>
                        <td className="px-2 py-1.5 font-semibold text-rose-700">AED {aed(a.total)}</td>
                        <td className="px-2 py-1.5">AED {aed(a.contract_loan)}</td>
                        <td className="px-2 py-1.5">AED {aed(a.total_recoveries)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy || !data || !picked.size} onClick={reverse} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="arrears-auto-settled-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Set {picked.size} back to Pending (AED {aed(pickedTotal)})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
